/**
 * Date formatting utilities with proper timezone handling
 * API dates are stored in UTC but may lack 'Z' suffix
 */

/**
 * Parse a date string from the API and ensure proper UTC interpretation
 * @param {string} dateString - Date string from API (e.g., "2025-12-12T15:18:41")
 * @returns {Date} - Date object in local timezone
 */
export const parseApiDate = (dateString) => {
  if (!dateString) return null;

  // If the date string doesn't have timezone info, assume UTC
  if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
    dateString = dateString + 'Z';
  }

  return new Date(dateString);
};

/**
 * Format a date to MM/DD/YYYY in user's local timezone
 * @param {string|Date} date - Date string from API or Date object
 * @returns {string} - Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '-';

  const dateObj = typeof date === 'string' ? parseApiDate(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) return '-';

  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const year = dateObj.getFullYear();

  return `${month}/${day}/${year}`;
};

/**
 * Format a date to a localized string with time in user's timezone
 * @param {string|Date} date - Date string from API or Date object
 * @returns {string} - Localized date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return '-';

  const dateObj = typeof date === 'string' ? parseApiDate(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) return '-';

  return dateObj.toLocaleString();
};

/**
 * Format a date relative to now (e.g., "2 hours ago", "Yesterday")
 * @param {string|Date} date - Date string from API or Date object
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '-';

  const dateObj = typeof date === 'string' ? parseApiDate(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) return '-';

  const now = new Date();
  const diffMs = now - dateObj;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return formatDate(dateObj);
};

/**
 * Period options that match API expectations
 * Maps days to period values supported by API
 */
export const PERIOD_OPTIONS = [
  { value: "DAY", label: "Today", days: 1 },
  { value: "WEEK", label: "This week", days: 7 },
  { value: "MONTH", label: "This month", days: 30 },
  { value: "YEAR", label: "This year", days: 365 },
  { value: "CUSTOM", label: "Custom", days: null },
];

/**
 * Calculate period parameter for API requests based on days count
 * Maps days to API-supported period values (DAY, WEEK, MONTH, YEAR)
 * @param {number} days - Number of days to look back (default: 30 for MONTH)
 * @returns {string} - Period string for API (e.g., "MONTH", "WEEK", "DAY")
 */
export const calculatePeriod = (days = 30) => {
  if (!days || days <= 0) return 'MONTH';

  // Map days to period values
  if (days <= 1) return 'DAY';
  if (days <= 7) return 'WEEK';
  if (days <= 30) return 'MONTH';
  if (days <= 365) return 'YEAR';
  return 'MONTH'; // default fallback
};

/**
 * Calculate date range for search queries based on days
 * @param {number} daysAgo - Number of days to look back (default: 30)
 * @returns {object} - Object with fromDate and toDate strings in YYYY-MM-DD format
 */
export const calculateDateRange = (daysAgo = 30) => {
  const today = new Date();
  const fromDate = new Date(today);
  fromDate.setDate(today.getDate() - daysAgo);

  return {
    fromDate: fromDate.toISOString().split('T')[0],
    toDate: today.toISOString().split('T')[0],
  };
};
